import { useIsSSR } from "@faire/web/common/server-side-rendering/isSSR";
import { NotificationBell } from "@faire/web/slate/icons/foundation/NotificationBell";
import { getSpacing } from "@faire/web/slate/spacing";
import { responsiveMediaQueries } from "@faire/web/slate/Theme/responsiveMediaQueries";
import React, { useEffect, useState } from "react";
import styled, { keyframes, css } from "styled-components";

import { LeftStreak } from "@faire/retailer/components/Header/HeaderNotifications/__internal__/NotificationStreaks";

const ANIMATION_TIME = 2.5;

export const NotificationBellWithStreaks: React.FC<{
  shouldAnimate?: boolean;
  delayAnimationTime?: number;
}> = ({ shouldAnimate = false, delayAnimationTime }) => {
  const [delayedShouldAniamte, setDelayedShouldAnimate] = useState(false);
  const isSSR = useIsSSR();

  useEffect(() => {
    if (delayAnimationTime) {
      const timer = setTimeout(() => {
        setDelayedShouldAnimate(shouldAnimate ?? false);
      }, delayAnimationTime ?? 2000);

      return () => clearTimeout(timer);
    }
    return;
  }, [delayAnimationTime, shouldAnimate]);

  const shouldAnimateIconOrDelay = delayAnimationTime
    ? delayedShouldAniamte
    : shouldAnimate;

  if (isSSR) {
    return <BellIcon strokeWidth={1} fontSize="20px" $shouldAnimate={false} />;
  }

  return (
    <>
      <BellIcon
        strokeWidth={1}
        fontSize="20px"
        $shouldAnimate={shouldAnimateIconOrDelay}
      />
      {shouldAnimateIconOrDelay ? (
        <>
          <TopLeftStreak $shouldAnimate={shouldAnimateIconOrDelay} />
        </>
      ) : null}
    </>
  );
};

const ring = keyframes`
  0%, 100% {
    transform: rotate(0);
  }
  20%, 60% {
    transform: rotate(-10deg); // Left swing
  }
  40%, 80% {
    transform: rotate(10deg); // Right swing
  }
`;

const showLeftStreak = keyframes`
  0%, 40%, 80%, 100% {
    opacity: 0;
  }
  20%, 60% {
    opacity: 1; // Show when the bell swings left
  }
`;

const performAnimation = (
  animation: ReturnType<typeof keyframes>,
  time: number
) => css`
  animation: ${animation} ${time}s ease;
  animation-fill-mode: both;
`;

const TopLeftStreak = styled(LeftStreak)<{ $shouldAnimate: boolean }>`
  position: absolute;
  left: ${getSpacing(6)};
  top: ${getSpacing("1x")};
  ${responsiveMediaQueries.mobile} {
    left: 9px;
  }
  ${({ $shouldAnimate }) =>
    $shouldAnimate && performAnimation(showLeftStreak, ANIMATION_TIME)};
`;

const BellIcon = styled(NotificationBell)<{ $shouldAnimate: boolean }>`
  position: relative;
  ${({ $shouldAnimate }) =>
    $shouldAnimate && performAnimation(ring, ANIMATION_TIME)};
`;
